import { Box, RadioButtonGroup, Text } from 'grommet';
import { Feature } from '@turf/helpers';
import { TFunction } from 'i18next';
import React, { useCallback, useEffect, useState, memo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PracticeCheckboxColumn, PracticeCheckbox, shouldShowCheckbox } from './PracticeCheckbox';
import { useDispatch } from 'react-redux';
import { changeFieldEnrollmentStatus } from '../../../../redux/actions/map-actions/field/field-action';
import { SyncErrors } from '../../../../redux/actions/map-actions/field/field-helper';
import { useParams } from 'react-router-dom';
import { IProducer } from '../../../producer-list/interfaces';
import { useAppSelector } from '../../../../redux';
import { allowBoundaryEditAndDelete } from '../../service/common/utils';
import { PracticeTypeInput } from './FieldItemForms/PracticeTypeInput';
import { FieldSituationInput } from './FieldItemForms/FieldSituationInput';
import { PaymentOptionInput } from './FieldItemForms/PaymentOptionInput';
import { ItemHeader } from './FieldItemForms/ItemHeader';
import { UnenrollModal } from './FieldItemForms/UnenrollModal';
import { FieldClassificationActons } from '../classification/FieldClassificationActons';

interface FieldItemProps {
  t: TFunction;
  field: Feature;
  focusField: (field: Feature) => void;
  saveField: (field: Feature) => void;
  deleteField: (field: Feature) => void;
  hidden?: boolean;
  syncErrors: SyncErrors;
  country: string;
  upsellingBoundaryTool: boolean;
  userRegion: string;
  producerData: any;
  practicesOnContract: any;
}

export interface FormInputProps {
  field: Feature;
  options: any;
  saveField: (field: Feature) => void;
  country?: string;
}

export const FieldItem = memo(
  ({
    t,
    field,
    focusField,
    saveField,
    deleteField,
    syncErrors,
    country,
    hidden = false,
    userRegion,
    upsellingBoundaryTool,
    producerData,
    practicesOnContract,
  }: FieldItemProps) => {
    const { usStackTillageWithLegumeAddition, shouldLdAllowBoundaryEdit } = useFlags();
    const dispatch = useDispatch();
    const disablePracticeSelectionForRegularFlow =
      country === 'us' &&
      (!producerData?.caseStatusHistory?.find((i: any) => i.status === 'In Support') || !upsellingBoundaryTool) &&
      producerData?.StageName === 'Closed Won';
    const isUpsellingFlow = upsellingBoundaryTool && !!producerData?.caseStatusHistory?.find((i: any) => i.status === 'In Support');
    const hasICL = field.properties?.practices.indexOf('ICL') > -1;
    const [showUnenrollModal, setShowUnenrollModal] = useState<boolean>(false);
    const [practicesErrored, setPracticesErrored] = useState<boolean>(false);
    const fieldName = field.properties?.['name'];

    const hasPaymentError = !!syncErrors.paymentOptionErrors?.find((error: any) => error.id === field.id);

    const getRadioOption = (label: string, value: string | boolean, cy?: string, disabled?: boolean, color?: string) => ({
      label: <Text style={{ fontSize: '14px', fontWeight: 400, color }}>{label}</Text>,
      value,
      'data-cy': cy,
      disabled,
    });

    const formOptions = new Map<string, any>([
      // Practice Types
      ['rowCrop', { label: t(`fieldFlow.fieldItem.practiceType.options.rowCrop`), value: 'ROW_CROP', cy: `practice-type-rowCrop=${fieldName}` }],
      ['pasture', { label: t(`fieldFlow.fieldItem.practiceType.options.pasture`), value: 'PASTURE', cy: `practice-type-pasture=${fieldName}` }],
      // Ownership Options
      ['owned', { label: t(`fieldFlow.fieldItem.ownership.options.owned`), value: 'OWNED', cy: `ownership-owned=${fieldName}` }],
      ['leased', { label: t(`fieldFlow.fieldItem.ownership.options.leased`), value: 'LEASED', cy: `ownership-leased=${fieldName}` }],
      ['other', { label: t(`fieldFlow.fieldItem.ownership.options.other`), value: 'OTHER', cy: `ownership-other=${fieldName}` }],
      // Payment Options
      ['optionA', { label: t(`fieldFlow.fieldItem.paymentOption.options.optionA`), value: 'OPTION_A', cy: `payment-optionA=${fieldName}` }],
      ['optionB', { label: t(`fieldFlow.fieldItem.paymentOption.options.optionB`), value: 'OPTION_B', cy: `payment-optionB=${fieldName}` }],
      ['annual', { label: t(`fieldFlow.fieldItem.paymentOption.options.annual`), value: 'ANNUAL', cy: `payment-annual=${fieldName}` }],
      // Irrigation Options
      ['irrigated', { label: t(`fieldFlow.fieldItem.irrigation.options.irrigated`), value: true }],
      ['nonIrrigated', { label: t(`fieldFlow.fieldItem.irrigation.options.nonIrrigated`), value: false }],
      // Legume Options
      ['legume', { label: t(`fieldFlow.fieldItem.legume.options.legume`), value: true }],
      ['nonLegume', { label: t(`fieldFlow.fieldItem.legume.options.nonLgume`), value: false }],
      // Tillage Options
      ['reducedTillage', { label: t(`fieldFlow.fieldItem.tillage.options.reduced`), value: 'reduced' }],
      ['intensiveTillage', { label: t(`fieldFlow.fieldItem.tillage.options.intensive`), value: 'intensive' }],
    ]);

    const practiceTypes = ['rowCrop', 'pasture'].map((type) => {
      const { label, value, cy } = formOptions.get(type);
      return getRadioOption(label, value, cy);
    });

    const getFieldOwnershipOptions = useCallback(() => {
      const options = ['owned', 'leased'].map((type) => {
        const { label, value, cy } = formOptions.get(type);
        return getRadioOption(label, value, cy);
      });

      if (country !== 'br') {
        const { label, value, cy } = formOptions.get('other');
        options.push(getRadioOption(label, value, cy));
      }
      return options;
    }, [country, fieldName, t]);

    const getPaymentOptions = () => {
      const errorColor = hasPaymentError && !field.properties?.paymentOption ? '#D03450' : '';

      if (field.properties?.paymentOption === 'ANNUAL') {
        const { label, value, cy } = formOptions.get('annual');
        return [getRadioOption(label, value, cy, false, errorColor)];
      } else {
        return ['optionA', 'optionB'].map((type) => {
          const { label, value, cy } = formOptions.get(type);
          const disabled = type === 'optionB' && isLegumeSelected;
          return getRadioOption(label, value, cy, disabled, errorColor);
        });
      }
    };

    const getIrrigationOptions = () => {
      return ['irrigated', 'nonIrrigated'].map((type) => {
        const { label, value } = formOptions.get(type);
        return getRadioOption(label, value);
      });
    };

    const getLegumeOptions = () => {
      return ['legume', 'nonLegume'].map((type) => {
        const { label, value } = formOptions.get(type);
        return getRadioOption(label, value);
      });
    };

    const getTillageOptions = () => {
      return ['reducedTillage', 'intensiveTillage'].map((type) => {
        const { label, value } = formOptions.get(type);
        return getRadioOption(label, value);
      });
    };

    const practices = field?.properties?.practices || [];
    const events = ['legume', 'noTillage', 'coverCropping', 'reducedTillage'];
    const [isLegumeSelected, isNoTillageSelected, isCoverCroppingSelected, isReducedTillageSelected] = events.map((e) => practices.includes(e));

    const isDisabledForRegularFlow = (code: string) =>
      disablePracticeSelectionForRegularFlow && !practicesOnContract.find((i: any) => i.code === code)?.isApplied;

    const disableIT = isNoTillageSelected || (isLegumeSelected && !usStackTillageWithLegumeAddition) || isDisabledForRegularFlow('IT');
    const disableNT = isReducedTillageSelected || (isLegumeSelected && !usStackTillageWithLegumeAddition) || isDisabledForRegularFlow('NT');
    const disableCC = isLegumeSelected || isDisabledForRegularFlow('CC') || hasICL;
    const disableNRR = !practices?.length || isDisabledForRegularFlow('NRR');
    const disableNO = !practices?.length || isLegumeSelected || isDisabledForRegularFlow('NO');
    const disableLA =
      (isReducedTillageSelected && !usStackTillageWithLegumeAddition) ||
      (isNoTillageSelected && !usStackTillageWithLegumeAddition) ||
      isCoverCroppingSelected ||
      isDisabledForRegularFlow('LA') ||
      isUpsellingFlow;
    const disableRPBS = isDisabledForRegularFlow('RPBS') || hasICL;
    const disableRPF = isDisabledForRegularFlow('RPF') || hasICL;
    const disableRPIG = isDisabledForRegularFlow('RPIG') || hasICL;

    const { journeyId } = useParams();
    const producersWithGroups: IProducer[] = useAppSelector((state) => state.producer.processedProducerList);

    const allowBoundaryEdit = allowBoundaryEditAndDelete(shouldLdAllowBoundaryEdit, producersWithGroups, journeyId);

    const disabledDeletedBtn = !allowBoundaryEdit || !!field.properties?.editing;

    const getPracticeColumns = () => {
      const isRowCrop = field.properties?.['practiceType'] === 'ROW_CROP';
      const rowCropFirstColumn = [
        { value: 'reducedTillage', disabled: disableIT, notInFlow: isDisabledForRegularFlow('IT') },
        { value: 'nitrogenReduction', disabled: disableNRR, notInFlow: isDisabledForRegularFlow('NRR') },
        { value: 'nitrogenOptimization', disabled: disableNO, notInFlow: isDisabledForRegularFlow('NO') },
        { value: 'legume', disabled: disableLA, notInFlow: isDisabledForRegularFlow('LA') },
      ];
      const rowCropSecondColumn = [
        { value: 'noTillage', disabled: disableNT, notInFlow: isDisabledForRegularFlow('NT') },
        { value: 'coverCropping', disabled: disableCC, notInFlow: isDisabledForRegularFlow('CC') },
      ];
      const pastureFirstColumn = [
        { value: 'seeding', disabled: disableRPBS, notInFlow: isDisabledForRegularFlow('RPBS'), errored: practicesErrored },
        { value: 'fertilization', disabled: disableRPF, notInFlow: isDisabledForRegularFlow('RPF'), errored: practicesErrored },
      ];
      const pastureSecondColumn = [
        { value: 'grazing', disabled: disableRPIG, notInFlow: isDisabledForRegularFlow('RPIG'), errored: practicesErrored },
      ];

      const columns = isRowCrop ? [rowCropFirstColumn, rowCropSecondColumn] : [pastureFirstColumn, pastureSecondColumn];
      return (
        <>
          {columns.map((practices, idx) => (
            <PracticeCheckboxColumn
              key={idx}
              practices={practices}
              field={field}
              saveField={saveField}
              country={country}
              practicesOnContract={practicesOnContract}
            />
          ))}
        </>
      );
    };

    const getBrazilPracticesList = () => {
      const orderedPracticesList = ['coverPerennial', 'coverAnnual', 'inoculant', 'seeding', 'grazing', 'fertilization', 'ICL'];
      return (
        <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 0', width: '100%' }}>
          {orderedPracticesList
            .filter((prac) => shouldShowCheckbox(prac, field, country, practicesOnContract))
            .map((prac) => (
              <PracticeCheckbox
                key={field.id + prac}
                field={field}
                saveField={saveField}
                country='br'
                t={t}
                value={prac}
                disabled={false}
                errored={practicesErrored}
              />
            ))}
        </Box>
      );
    };

    useEffect(() => {
      setPracticesErrored(syncErrors.missingPractices.indexOf(field.id?.toString() || '') !== -1);
    }, [syncErrors, field.id]);

    const getLegumeMessage = () => (
      <>
        When <b style={{ fontWeight: 900 }}>Legume Addition</b> is applied{' '}
        <b style={{ fontWeight: 900 }}>
          {practicesOnContract.find((i: any) => i.code === 'NRR')
            ? 'only Nitrogen Reduction and Tillage practices can be selected'
            : 'only Tillage practices can be selected'}
        </b>{' '}
        and the only payment option available is <b style={{ fontWeight: 900 }}>Option A</b>
      </>
    );

    const handleUnenroll = (event: any) => {
      if (field.properties?.editing) return;
      event.stopPropagation();
      setShowUnenrollModal(true);
    };

    const handleDelete = (event: any) => {
      if (disabledDeletedBtn) return;
      event.stopPropagation();
      deleteField(field);
    };

    const handleSubmitModal = (event: any) => {
      event.stopPropagation();
      dispatch(changeFieldEnrollmentStatus(field, !field.properties?.unenrolledAt, hidden));
      setShowUnenrollModal(false);
    };

    const baseStyle = { fontSize: '12px', fontWeight: 400, color: '#282828', textAlign: 'center' as const };
    const legumeMsgStyle = { ...baseStyle, fontFamily: 'Avenir', lineHeight: '14px', letterSpacing: '0.07em', padding: '.5rem' };
    const titleStyle = { ...baseStyle, fontSize: '16px', lineHeight: '24px', textAlign: 'start' as const };

    return (
      <Box align='start'>
        {!!field.properties?.ineligibleAreaAcres && (
          <Box
            style={{
              background: '#D03450',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              marginTop: '10px',
            }}>
            <Text data-cy={`field-wetlands-overlap-${field.id}`} style={{ color: '#fff', fontWeight: 900, fontSize: '16px', lineHeight: '20px' }}>
              Ineligible area conflicts removed (
              {!field.properties?.area_unit || field.properties?.area_unit.toLowerCase() === 'acres'
                ? Number.parseFloat(field.properties?.ineligibleAreaAcres).toFixed(2)
                : Number.parseFloat(field.properties?.ineligibleAreaHectares).toFixed(2)}{' '}
              {field.properties?.area_unit || 'acres'})
            </Text>
          </Box>
        )}

        <Box style={{ width: '100%' }}>
          <Box
            id={`field-${field.id}`}
            key={`field-${field.id}`}
            border={{ side: 'bottom', color: '#ccc', size: '1px' }}
            style={{ width: '100%', paddingBottom: '16px', minHeight: 'auto', gap: '16px' }}
            focusIndicator={false}
            onClick={() => focusField(field)}>
            <ItemHeader
              field={field}
              saveField={saveField}
              handleDelete={handleDelete}
              producerData={producerData}
              handleUnenroll={handleUnenroll}
              userRegion={userRegion}
              allowBoundaryEdit={shouldLdAllowBoundaryEdit}
              disabledBtn={disabledDeletedBtn}
            />

            <FieldClassificationActons field={field} />

            {showUnenrollModal && <UnenrollModal hideModal={() => setShowUnenrollModal(false)} handleSubmit={handleSubmitModal} />}

            {field.properties?.['practices'] && field.properties['practices'].indexOf('legume') !== -1 && (
              <Box direction={'row'} align={'center'} style={{ background: 'rgb(208, 52, 80, 0.2)', borderRadius: '10px', marginBottom: '4px' }}>
                <Text style={legumeMsgStyle}>{getLegumeMessage()}</Text>
              </Box>
            )}

            <FieldSituationInput field={field} saveField={saveField} options={getFieldOwnershipOptions()} />

            <PracticeTypeInput field={field} saveField={saveField} options={practiceTypes} country={country} />

            <PaymentOptionInput field={field} saveField={saveField} options={getPaymentOptions()} country={country} />

            <Box gap={'4px'}>
              <Text style={titleStyle}>{t('fieldFlow.fieldItem.irrigation.title')}</Text>

              <RadioButtonGroup
                direction={'row'}
                data-cy={`field-irrigated-${field.properties?.['name']}`}
                name={'irrigated'}
                value={field.properties?.['irrigated']}
                onChange={() => saveField({ ...field, properties: { ...field.properties, irrigated: !field.properties!['irrigated'] } })}
                options={getIrrigationOptions()}
              />
            </Box>

            <Box gap={'8px'} id={'practices-' + field.id}>
              <Text style={titleStyle}>{t('fieldFlow.fieldItem.practices.title')}</Text>
              <Box direction={'row'}>{country !== 'br' ? <>{getPracticeColumns()}</> : getBrazilPracticesList()}</Box>
            </Box>
            {field.properties?.['practices'] && field.properties['practices'].indexOf('coverCropping') !== -1 && country !== 'br' && (
              <Box gap={'4px'}>
                <Text style={titleStyle}>{t('fieldFlow.fieldItem.legume.title')}</Text>
                <RadioButtonGroup
                  direction={'row'}
                  name={'legume'}
                  value={field.properties?.['legume']}
                  onChange={(event) => saveField({ ...field, properties: { ...field.properties, legume: event.target.value === 'true' } })}
                  options={getLegumeOptions()}
                />
              </Box>
            )}
            {field.properties?.['practices'] &&
              (field.properties['practices'].indexOf('noTillage') !== -1 || field.properties['practices'].indexOf('reducedTillage') !== -1) && (
                <Box gap={'4px'}>
                  <Text style={titleStyle}>{t('fieldFlow.fieldItem.tillage.title')}</Text>

                  <RadioButtonGroup
                    direction={'row'}
                    name={'previousTillage'}
                    value={field.properties?.['previousTillage']}
                    onChange={(event) => saveField({ ...field, properties: { ...field.properties, previousTillage: event.target.value } })}
                    options={getTillageOptions()}
                  />
                </Box>
              )}
          </Box>
        </Box>
      </Box>
    );
  },
);
